<template>
  <div class="filters-wrapper">
    <!-- Filtro de Data -->
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{
              formattedDateRange ||
              $t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECT_DATE_RANGE')
            }}
          </button>
        </template>
      </DatePicker>
    </div>

    <!-- Filtro: Caixa de Entrada -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? $t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECTED_INBOXES', {
                  count: selectedInboxes.length,
                })
              : $t('TEMPLATE_EVENTS_REPORT.TABLE.INBOX')
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            :placeholder="
              $t('TEMPLATE_EVENTS_REPORT.FILTERS.INBOX_SEARCH_PLACEHOLDER')
            "
          />
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox"
            class="checkbox-option"
          >
            <label>
              <input
                type="checkbox"
                :value="inbox"
                v-model="selectedInboxes"
                @change="emitFilters"
              />
              {{ inbox }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro: Usuário -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('user')">
          {{
            selectedUsers.length
              ? $t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECTED_USERS', {
                  count: selectedUsers.length,
                })
              : $t('TEMPLATE_EVENTS_REPORT.TABLE.USER')
          }}
        </button>
        <div v-if="openDropdown === 'user'" class="dropdown-content">
          <input
            type="text"
            v-model="userSearch"
            class="search-input"
            :placeholder="$t('TEMPLATE_EVENTS_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="u in filteredUsers" :key="u" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="u"
                v-model="selectedUsers"
                @change="emitFilters"
              />
              {{ u }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro: Status da mensagem -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('status')">
          {{
            selectedStatuses.length
              ? $t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECTED_STATUSES', {
                  count: selectedStatuses.length,
                })
              : $t('TEMPLATE_EVENTS_REPORT.FILTERS.MESSAGE_STATUS')
          }}
        </button>
        <div v-if="openDropdown === 'status'" class="dropdown-content">
          <div
            class="checkbox-option"
            v-for="status in filteredStatuses"
            :key="status"
          >
            <label>
              <input
                type="checkbox"
                :value="status"
                v-model="selectedStatuses"
                @change="emitFilters"
              />
              {{ status }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtro: Template -->
    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('template')">
          {{
            selectedTemplateNames.length
              ? $t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECTED_TEMPLATES', {
                  count: selectedTemplateNames.length,
                })
              : $t('TEMPLATE_EVENTS_REPORT.TABLE.TEMPLATE')
          }}
        </button>
        <div v-if="openDropdown === 'template'" class="dropdown-content">
          <input
            type="text"
            v-model="templateSearch"
            class="search-input"
            :placeholder="
              $t('TEMPLATE_EVENTS_REPORT.FILTERS.TEMPLATE_SEARCH_PLACEHOLDER')
            "
          />
          <div
            class="checkbox-option"
            v-for="name in filteredTemplateNames"
            :key="name"
          >
            <label>
              <input
                type="checkbox"
                :value="name"
                v-model="selectedTemplateNames"
                @change="emitFilters"
              />
              {{ name }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'TemplateEventsFilters',
  components: { DatePicker },
  props: {
    templateEventsData: { type: Array, required: true },
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      tempDateRange: [sevenDaysAgo, today],
      customDateRange: [sevenDaysAgo, today],
      openDropdown: null,

      selectedInboxes: [],
      selectedUsers: [],
      selectedStatuses: [],
      selectedTemplateNames: [],

      inboxSearch: '',
      userSearch: '',
      templateSearch: '',
    };
  },
  computed: {
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end)
        return this.$t('TEMPLATE_EVENTS_REPORT.FILTERS.SELECT_DATE_RANGE');
      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      return `${format(start)} - ${format(end)}`;
    },
    filteredInboxes() {
      return [
        ...new Set(
          this.templateEventsData.map(i => i.inbox_name).filter(Boolean)
        ),
      ].filter(i => i.toLowerCase().includes(this.inboxSearch.toLowerCase()));
    },
    filteredUsers() {
      return [
        ...new Set(
          this.templateEventsData.map(i => i.sender_name).filter(Boolean)
        ),
      ].filter(i => i.toLowerCase().includes(this.userSearch.toLowerCase()));
    },
    filteredStatuses() {
      return [
        ...new Set(this.templateEventsData.map(i => i.status).filter(Boolean)),
      ];
    },
    filteredTemplateNames() {
      return [
        ...new Set(
          this.templateEventsData.map(i => i.template_name).filter(Boolean)
        ),
      ].filter(name =>
        name.toLowerCase().includes(this.templateSearch.toLowerCase())
      );
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;

        this.selectedInboxes = [];
        this.selectedUsers = [];
        this.selectedStatuses = [];
        this.selectedTemplateNames = [];

        this.emitFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    emitFilters() {
      this.$emit('filter-change', {
        customDateRange: this.customDateRange,
        inboxes: this.selectedInboxes,
        users: this.selectedUsers,
        messageStatuses: this.selectedStatuses,
        templates: this.selectedTemplateNames,
      });
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      return date < this.minSelectableDate || date > this.maxSelectableDate;
    },
  },
};
</script>

<style>
.filters-wrapper {
  width: 280px;
  background-color: #000;
  padding: 20px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-option label {
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

::v-deep(.dp__input_icon),
.mx-icon-calendar,
.mx-icon-clear {
  display: none !important;
}
</style>
